import { ArrowRight, CheckCircle2, ChevronDown, XCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/shared/utils/utils';

import type { RoiDataset, RoiProject } from '../types/roi';

interface RoiAuditLogProps {
  dataset: RoiDataset;
}

function projectImpactScore(project: RoiProject): number {
  return (
    project.reworkEngineerDays * 2 +
    project.weeksSlipped * 5 +
    project.firefightEngineerDays * 3
  );
}

function sortByImpact(projects: RoiProject[]): RoiProject[] {
  return [...projects].sort(
    (left, right) => projectImpactScore(right) - projectImpactScore(left)
  );
}

export function RoiAuditLog({ dataset }: RoiAuditLogProps) {
  const heroProjects = dataset.projects.filter(
    (project) => project.tier === 'hero'
  );
  const standardProjects = dataset.projects.filter(
    (project) => project.tier !== 'hero'
  );
  const tailCaught = sortByImpact(
    standardProjects.filter((project) => project.verdict === 'caught')
  );
  const tailMissed = sortByImpact(
    standardProjects.filter((project) => project.verdict === 'missed')
  );
  const recallPct = Math.round(
    (dataset.caughtProjects / dataset.totalProjects) * 100
  );

  return (
    <section className="flex flex-col gap-5">
      <HeroSpotlights heroProjects={heroProjects} />

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          The other {standardProjects.length} projects
        </h3>
        <span className="text-[11px] tabular-nums text-slate-400">
          {dataset.caughtProjects} / {dataset.totalProjects} ({recallPct}%)
          recall overall
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TailBucket
          variant="caught"
          projects={tailCaught}
          heroCount={heroProjects.length}
        />
        <TailBucket variant="missed" projects={tailMissed} heroCount={0} />
      </div>
    </section>
  );
}

const HERO_SPAN_BY_INDEX: Record<number, string> = {
  0: 'lg:col-span-7',
  1: 'lg:col-span-5',
};

interface HeroSpotlightsProps {
  heroProjects: RoiProject[];
}

function HeroSpotlights({ heroProjects }: HeroSpotlightsProps) {
  if (heroProjects.length === 0) {
    return null;
  }
  const orderedHeroes = [...heroProjects].sort(
    (left, right) => right.weeksSlipped - left.weeksSlipped
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[20px] font-bold leading-tight tracking-tight text-slate-900">
          {orderedHeroes.length} costliest projects{' '}
          <span className="text-red-600">- months of stolen engineering</span>
        </h3>
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
          Sorted by deadline slip
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        {orderedHeroes.map((project, index) => {
          const spanClass = HERO_SPAN_BY_INDEX[index] ?? 'lg:col-span-4';
          const variant: HeroVariant = index < 2 ? 'feature' : 'compact';
          return (
            <div key={project.id} className={spanClass}>
              <HeroSpotlightCard project={project} variant={variant} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

type HeroVariant = 'feature' | 'compact';

const PLANNED_WEEKS_ASSUMPTION = 13;

interface HeroSpotlightCardProps {
  project: RoiProject;
  variant: HeroVariant;
}

function HeroSpotlightCard({ project, variant }: HeroSpotlightCardProps) {
  const narrativeLimit = variant === 'feature' ? 140 : 80;
  const narrativePreview =
    project.narrative && project.narrative.length > narrativeLimit
      ? `${project.narrative.slice(0, narrativeLimit).trimEnd()}…`
      : project.narrative ?? '';
  const titleClass =
    variant === 'feature'
      ? 'text-base font-semibold leading-snug text-slate-900'
      : 'text-sm font-semibold leading-snug text-slate-900';
  const reworkDaysPerEngineer = Math.round(
    project.reworkEngineerDays / project.headcount
  );
  const plannedBarPct =
    (PLANNED_WEEKS_ASSUMPTION /
      (PLANNED_WEEKS_ASSUMPTION + project.weeksSlipped)) *
    100;
  const slipBarPct = 100 - plannedBarPct;
  return (
    <article className="group flex h-full flex-col gap-2.5 overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_0_rgba(15,23,42,0.04)] transition-colors hover:border-red-200">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
            {project.team}
          </span>
          <h3 className={cn(titleClass, 'mt-1')}>{project.name}</h3>
        </div>
        <div className="shrink-0 text-end">
          <div className="text-2xl font-bold leading-none tabular-nums text-red-600">
            +{project.weeksSlipped}
            <span className="ms-0.5 text-sm font-bold">wk</span>
          </div>
          <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-red-500">
            late
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-1.5">
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="bg-slate-300"
            style={{ width: `${plannedBarPct}%` }}
            aria-label="Planned timeline"
          />
          <div
            className="bg-red-500"
            style={{ width: `${slipBarPct}%` }}
            aria-label="Slip"
          />
        </div>
        <div className="flex items-center justify-between text-[10px] tabular-nums text-slate-500">
          <span>Planned: {project.plannedShip}</span>
          <span className="font-semibold text-red-600">
            Actual: {project.actualShip}
          </span>
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-slate-500">
        {narrativePreview}
      </p>

      <div className="mt-auto grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-[11px]">
        <SpotlightStat
          label="Rework / engineer"
          value={`${reworkDaysPerEngineer}d`}
        />
        <SpotlightStat
          label="Engineers stuck on it"
          value={`${project.headcount}`}
        />
        <div className="col-span-2 mt-0.5 border-t border-slate-200 pt-2 text-[10px] tabular-nums text-slate-600">
          <span className="font-semibold text-red-600">
            {project.reworkEngineerDays}d
          </span>{' '}
          of engineer-time burned on rework instead of new product work.
        </div>
      </div>

      <Link
        to="/roi-demo/projects/$projectId"
        params={{ projectId: project.id }}
        className="inline-flex items-center gap-1.5 self-start text-[11px] font-semibold text-[rgb(0,115,234)] transition-colors hover:text-[rgb(0,90,200)]"
      >
        Read the full story
        <ArrowRight className="h-3 w-3" />
      </Link>
    </article>
  );
}

interface SpotlightStatProps {
  label: string;
  value: string;
  accent?: boolean;
}

function SpotlightStat({ label, value, accent }: SpotlightStatProps) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd
        className={cn(
          'mt-0.5 text-base font-bold tabular-nums',
          accent ? 'text-red-600' : 'text-slate-700'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

type TailVariant = 'caught' | 'missed';

const VARIANT_STYLE: Record<
  TailVariant,
  {
    accent: string;
    accentDot: string;
    label: string;
    icon: typeof CheckCircle2;
    iconClass: string;
  }
> = {
  caught: {
    accent: 'border-emerald-200/80',
    accentDot: 'bg-emerald-500',
    label: 'Caught',
    icon: CheckCircle2,
    iconClass: 'text-emerald-600',
  },
  missed: {
    accent: 'border-rose-200/80',
    accentDot: 'bg-rose-500',
    label: 'Missed',
    icon: XCircle,
    iconClass: 'text-rose-600',
  },
};

const MISSED_REASON_KEYWORDS: Array<{ label: string; pattern: RegExp }> = [
  {
    label: 'Vendor / partner failure',
    pattern: /vendor|sdk|partner|acquired|sandbox|sla/i,
  },
  {
    label: 'Regulatory / legal',
    pattern: /regulator|regulatory|legal|gdpr|compliance|soc 2|fedramp|policy/i,
  },
  {
    label: 'Strategy / cancellation',
    pattern: /strateg|pivot|leadership|de-prioritiz|competitor|kill/i,
  },
  {
    label: 'Customer / external scheduling',
    pattern: /customer|reorganiz|budget cycle|procurement/i,
  },
  {
    label: 'Key-person / scoping',
    pattern: /key-person|attrition|tech lead|scoping|spec|deprecated/i,
  },
];

function categorizeMissedRationale(rationale: string): string {
  for (const { label, pattern } of MISSED_REASON_KEYWORDS) {
    if (pattern.test(rationale)) {
      return label;
    }
  }
  return 'Other external';
}

function buildMissedReasonCounts(
  projects: RoiProject[]
): Array<[string, number]> {
  const counts = new Map<string, number>();
  for (const project of projects) {
    const reason = categorizeMissedRationale(project.verdictRationale);
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return [...counts.entries()].sort(
    ([, leftCount], [, rightCount]) => rightCount - leftCount
  );
}

interface TailBucketProps {
  variant: TailVariant;
  projects: RoiProject[];
  heroCount: number;
}

function TailBucket({ variant, projects, heroCount }: TailBucketProps) {
  const style = VARIANT_STYLE[variant];
  const Icon = style.icon;

  const reworkCount = projects.filter(
    (project) => project.reworkEngineerDays > 0
  ).length;
  const lateCount = projects.filter(
    (project) => project.weeksSlipped > 0
  ).length;
  const firefightCount = projects.filter(
    (project) => project.firefightEngineerDays > 0
  ).length;

  const teamCounts = new Map<string, number>();
  for (const project of projects) {
    teamCounts.set(project.team, (teamCounts.get(project.team) ?? 0) + 1);
  }
  const topTeams = [...teamCounts.entries()]
    .sort(([, leftCount], [, rightCount]) => rightCount - leftCount)
    .slice(0, 4);

  const reasonCounts =
    variant === 'missed' ? buildMissedReasonCounts(projects) : null;

  return (
    <article
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]',
        style.accent
      )}
    >
      <header className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4', style.iconClass)} />
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
            {style.label}
          </span>
        </div>
        <div className="flex items-baseline gap-1.5 tabular-nums">
          <span className="text-2xl font-bold text-slate-900">
            {projects.length}
          </span>
          {heroCount > 0 && (
            <span className="text-[11px] text-slate-400">
              + {heroCount} above
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-5 px-5 py-4 md:grid-cols-2">
        {variant === 'caught' ? (
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Failure modes
            </span>
            <ul className="mt-2 space-y-1.5 text-[12px] text-slate-700">
              <BreakdownRow
                label="Rework during build"
                count={reworkCount}
                total={projects.length}
                dotClass={style.accentDot}
              />
              <BreakdownRow
                label="Late shipping"
                count={lateCount}
                total={projects.length}
                dotClass={style.accentDot}
              />
              <BreakdownRow
                label="Post-launch firefight"
                count={firefightCount}
                total={projects.length}
                dotClass={style.accentDot}
              />
            </ul>
          </div>
        ) : (
          <div>
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Why we miss them
            </span>
            <ul className="mt-2 space-y-1.5 text-[12px] text-slate-700">
              {reasonCounts?.map(([reason, count]) => (
                <BreakdownRow
                  key={reason}
                  label={reason}
                  count={count}
                  total={projects.length}
                  dotClass={style.accentDot}
                />
              ))}
            </ul>
          </div>
        )}

        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Top teams
          </span>
          <ul className="mt-2 space-y-1.5 text-[12px] text-slate-700">
            {topTeams.map(([team, count]) => (
              <li
                key={team}
                className="flex items-center justify-between gap-3"
              >
                <span className="truncate">{team}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                  {count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <details className="group border-t border-gray-100">
        <summary className="flex cursor-pointer items-center justify-between px-5 py-2.5 text-[11px] font-semibold text-slate-500 transition-colors hover:bg-slate-50/50 [&::-webkit-details-marker]:hidden">
          <span>Browse all {projects.length} projects</span>
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
        </summary>
        <ul className="max-h-[260px] divide-y divide-gray-50 overflow-y-auto bg-slate-50/30">
          {projects.map((project) => (
            <li
              key={project.id}
              className="flex items-center justify-between gap-3 px-5 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-[12px] font-medium text-slate-800">
                  {project.name}
                </div>
                <div className="truncate text-[10px] text-slate-400">
                  {project.team} · {project.plannedShip} → {project.actualShip}
                </div>
              </div>
              <CompactImpact project={project} />
            </li>
          ))}
        </ul>
      </details>
    </article>
  );
}

interface BreakdownRowProps {
  label: string;
  count: number;
  total: number;
  dotClass: string;
}

function BreakdownRow({ label, count, total, dotClass }: BreakdownRowProps) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="flex items-center gap-2 truncate">
        <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dotClass)} />
        <span className="truncate">{label}</span>
      </span>
      <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
        {count} <span className="text-slate-300">({pct}%)</span>
      </span>
    </li>
  );
}

interface CompactImpactProps {
  project: RoiProject;
}

function CompactImpact({ project }: CompactImpactProps) {
  const parts: string[] = [];
  if (project.reworkEngineerDays > 0) {
    parts.push(`${project.reworkEngineerDays}d rework`);
  }
  if (project.weeksSlipped > 0) {
    parts.push(`${project.weeksSlipped}wk slip`);
  }
  if (project.firefightEngineerDays > 0) {
    parts.push(`${project.firefightEngineerDays}d firefight`);
  }
  if (parts.length === 0) {
    return <span className="text-[10px] text-slate-300">-</span>;
  }
  return (
    <span className="shrink-0 text-[10px] tabular-nums text-slate-500">
      {parts.join(' · ')}
    </span>
  );
}
